import { P2GBox, P2GGrid } from '@parcel2go/core-ui';
import { P2GRadio } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';

import { PaymentOptionApiModel, PaymentOptionOptionApiModel, PaymentOptionType } from '~/lib/api/smartsend/types';
import { ProviderGroup } from '../../../../lib/helpers/SmartSendBulkActionApplier';
import PaymentLabel from './PaymentLabel';
import { P2GDropdown } from '@parcel2go/core-ui';
import ProviderGroupPromoCode from './ProviderGroupPromoCode';
import ProviderGroupSummaryTotal from './ProviderGroupSummaryTotal';
import P2GOptionContainer from '~/components/option-container/option-container';
import { ContentGroups, TranslationNamespaces } from '~/lib/constants';
import { Trans, useTranslation } from 'react-i18next';
import { Suspense, useEffect, useMemo, useRef } from 'react';
import StandaloneCmsContent from '~/components/content/standalone-cms-content';
import { findContentEntry, useCmsContentGroup } from '~/hooks/use-cms-content-group';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { EventSections } from '../../../../lib/events/EventLogger';

/** Marketing-managed card / PayPal Pay-Later logos shown beneath the "Other Payment Options" box. */
const PAYMENT_LOGOS_CONTENT_KEY = 'content-payment-options-logos';

interface props {
  group: ProviderGroup;
  updateSelectedPaymentOption: (option: PaymentOptionType, optionOption: PaymentOptionOptionApiModel) => void;
  updateDiscountCode: (discountCode: string) => void;
  paymentOptions: PaymentOptionApiModel[];
  close: () => void;
  breakdown: Record<string, number> | null;
  verifying: boolean;
}

/**
 * A PrePay balance that cannot cover the order is offered but not selectable. Shared by the radio's
 * `disabled` prop and the `payment_options_shown` payload so the two can never disagree.
 */
const isOptionDisabled = (option: PaymentOptionApiModel, group: ProviderGroup) =>
  option.Type === 'TopUp' && Number(option.Balance) < Number(group.TotalPrice);

const ProviderGroupSummaryIdle: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { track } = useSmartSendAnalytics();
  const { entries, isLoading: contentLoading } = useCmsContentGroup(ContentGroups.PAYMENTS);
  const trackedGroupKey = useRef<string | null>(null);

  const showOptions = props.paymentOptions.length > 1;
  const hasMarketingContent = !!findContentEntry(entries, PAYMENT_LOGOS_CONTENT_KEY);

  const disabledOptions = useMemo(
    () => props.paymentOptions.filter((o) => isOptionDisabled(o, props.group)).map((o) => o.Type),
    [props.paymentOptions, props.group]
  );

  useEffect(() => {
    // One impression per time this group is viewed, not per render - the panel re-renders on promo
    // codes and verify-ship responses. Note the ref lives with the component instance, and
    // ProviderGroupSummary unmounts a group when it is not the visible one, so switching between
    // provider-group tabs and back deliberately counts as a fresh impression.
    // Held back until the CMS group settles (data or error), otherwise the first render would
    // report hasMarketingContent: false and the guard below would stop it ever being corrected.
    // The same request gates whether the logos render at all, so nothing is attributable while
    // it is still in flight.
    if (!showOptions || contentLoading || trackedGroupKey.current === props.group.Key) {
      return;
    }
    trackedGroupKey.current = props.group.Key;
    track({
      type: 'payment_options_shown',
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.Book]),
        options: props.paymentOptions.map((o) => o.Type),
        disabledOptions,
        optionCount: props.paymentOptions.length,
        defaultSelected: props.group.SelectedPaymentMethod,
        provider: props.group.Provider.Provider,
        totalPrice: props.group.TotalPrice,
        currencyCode: props.group.CurrencyCode,
        hasMarketingContent
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOptions, contentLoading, props.group.Key, hasMarketingContent]);

  return (
    <>
      <ProviderGroupSummaryTotal group={props.group} breakdown={props.breakdown} verifying={props.verifying} />
      <ProviderGroupPromoCode updateDiscountCode={props.updateDiscountCode} group={props.group} />
      {showOptions && (
        <P2GBox mt={2}>
          <P2GText component="p" colour="primary">
            <Trans i18nKey="PAYMENT_OPTIONS_SUBTTILE" t={t} defaults="<strong>Payment Options</strong>" />
          </P2GText>
          <P2GGrid container direction="column" gap={2} mt={2}>
            {props.paymentOptions.map((o) => (
              <P2GGrid key={o.Type}>
                <P2GOptionContainer variant="payment-option" selected={o.Type === props.group.SelectedPaymentMethod}>
                  <P2GRadio
                    name="SelectedPaymentOption"
                    value={o.Type}
                    disabled={isOptionDisabled(o, props.group)}
                    checked={o.Type === props.group.SelectedPaymentMethod}
                    onChange={() =>
                      props.updateSelectedPaymentOption(o.Type, props.group.SelectedPaymentOption || o.Options[0])
                    }
                    id="Selected"
                    label={
                      <PaymentLabel
                        close={props.close}
                        paymentType={o}
                        option={props.group.SelectedPaymentOption}
                        group={props.group}
                      />
                    }
                  />
                  {o.Type === props.group.SelectedPaymentMethod && o.Options.length > 0 && (
                    <P2GBox>
                      <P2GDropdown
                        id="Options"
                        value={props.group.SelectedPaymentOption?.Id}
                        options={o.Options.map((x) => {
                          return { value: x.Id, text: x.Display };
                        })}
                        onChange={(v) =>
                          props.updateSelectedPaymentOption(
                            o.Type,
                            o.Options.find((o) => o.Id === v)
                          )
                        }
                        label={t('SELECT_PAYMENT_OPTION', { defaultValue: 'Select Payment Option' })}
                      />
                    </P2GBox>
                  )}
                </P2GOptionContainer>
                {o.Type === 'External' && (
                  <Suspense>
                    <StandaloneCmsContent
                      group={ContentGroups.PAYMENTS}
                      contentKey={PAYMENT_LOGOS_CONTENT_KEY}
                      wrapper={(c) => <P2GBox mt={1}>{c}</P2GBox>}
                    />
                  </Suspense>
                )}
              </P2GGrid>
            ))}
          </P2GGrid>
        </P2GBox>
      )}
    </>
  );
};

export default ProviderGroupSummaryIdle;
